import { useState, useEffect, useRef, useCallback } from 'react';
import { Bell, X, Users, Info, Check, Trash2, Crosshair } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useApi } from '../../lib/useApi';

interface Notification {
  id: string;
  type: string;
  title: string;
  message: string;
  read: boolean;
  action_url: string;
  metadata: Record<string, string>;
  created_at: string;
}

function timeAgo(date: string): string {
  const seconds = Math.floor((Date.now() - new Date(date).getTime()) / 1000);
  if (seconds < 60) return 'just now';
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days < 7) return `${days}d ago`;
  return new Date(date).toLocaleDateString();
}

const typeIcon: Record<string, typeof Info> = {
  join_request: Users,
  account_assigned: Users,
  info: Info,
  mission_approved: Crosshair,
  mission_changes_requested: Crosshair,
  mission_reviewed: Crosshair,
};

export default function NotificationBell({ collapsed }: { collapsed: boolean }) {
  const { user, organisation } = useAuth();
  const { apiGet, apiPut, apiPost } = useApi();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const panelRef = useRef<HTMLDivElement>(null);

  const load = useCallback(async () => {
    if (!user || !organisation) return;
    try {
      const { notifications: data } = await apiGet<{ notifications: Notification[] }>(
        `/v1/notifications?org_id=${organisation.id}`,
      );
      if (data) {
        setNotifications(data);
        setUnreadCount(data.filter(n => !n.read).length);
      }
    } catch { /* non-critical, silently skip */ }
  }, [user, organisation, apiGet]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (panelRef.current && !panelRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    if (open) document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [open]);

  const markRead = async (id: string) => {
    try {
      await apiPut(`/v1/notifications/${id}`, { read: true });
    } catch { /* ignore */ }
    setNotifications(prev => prev.map(n => n.id === id ? { ...n, read: true } : n));
    setUnreadCount(prev => Math.max(0, prev - 1));
  };

  const markAllRead = async () => {
    if (!organisation) return;
    const hasUnread = notifications.some(n => !n.read);
    if (!hasUnread) return;
    try {
      await apiPost('/v1/notifications/read-all', { org_id: organisation.id });
    } catch { /* ignore */ }
    setNotifications(prev => prev.map(n => ({ ...n, read: true })));
    setUnreadCount(0);
  };

  const handleClick = (notification: Notification) => {
    if (!notification.read) markRead(notification.id);
    if (notification.action_url) {
      setOpen(false);
      navigate(notification.action_url);
    }
  };

  return (
    <div className="relative" ref={panelRef}>
      <button
        onClick={() => setOpen(!open)}
        className="app-focus-ring flex items-center gap-3 w-full px-3 py-2.5 rounded-lg text-sm font-medium text-app-chrome-muted hover:bg-white/[0.06] hover:text-white transition duration-200 relative"
        aria-expanded={open}
      >
        <Bell className="w-5 h-5 flex-shrink-0" />
        {!collapsed && <span>Notifications</span>}
        {unreadCount > 0 && (
          <span className={`${collapsed ? 'absolute top-1 right-1' : 'ml-auto'} min-w-[18px] h-[18px] px-1 flex items-center justify-center text-[10px] font-bold text-white bg-app-critical rounded-full`}>
            {unreadCount > 99 ? '99+' : unreadCount}
          </span>
        )}
      </button>

      {open && (
        <div className="fixed inset-x-4 top-16 z-50 max-h-[calc(100vh-5rem)] overflow-hidden rounded-lg border border-app-line bg-app-surface md:absolute md:inset-x-auto md:bottom-full md:left-full md:top-auto md:mb-0 md:ml-2 md:w-96">
          <div className="p-4 border-b border-app-line flex items-center justify-between">
            <h3 className="text-sm font-semibold text-app-ink">Notifications</h3>
            <div className="flex items-center gap-2">
              {unreadCount > 0 && (
                <button
                  onClick={markAllRead}
                  className="text-[11px] text-forest-600 hover:text-forest-700 font-medium"
                >
                  Mark all read
                </button>
              )}
              <button onClick={() => setOpen(false)} className="p-1 text-app-muted hover:text-app-muted rounded-lg hover:bg-app-canvas transition-colors">
                <X className="w-4 h-4" />
              </button>
            </div>
          </div>

          <div className="max-h-[400px] overflow-y-auto">
            {notifications.length === 0 ? (
              <div className="p-8 text-center">
                <Bell className="w-8 h-8 text-app-line mx-auto mb-2" />
                <p className="text-sm text-app-muted">No notifications yet</p>
              </div>
            ) : (
              <div className="divide-y divide-app-line">
                {notifications.map(n => {
                  const Icon = typeIcon[n.type] || Info;
                  return (
                    <button
                      key={n.id}
                      onClick={() => handleClick(n)}
                      className={`w-full flex items-start gap-3 p-4 text-left hover:bg-app-canvas transition-colors ${!n.read ? 'bg-app-focus/10' : ''}`}
                    >
                      <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${
                        !n.read ? 'bg-forest-100' : 'bg-app-canvas'
                      }`}>
                        <Icon className={`w-4 h-4 ${!n.read ? 'text-forest-600' : 'text-app-muted'}`} />
                      </div>
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2">
                          <p className={`text-sm truncate ${!n.read ? 'font-semibold text-app-ink' : 'font-medium text-app-ink'}`}>
                            {n.title}
                          </p>
                          {!n.read && <span className="w-2 h-2 rounded-full bg-forest-500 flex-shrink-0" />}
                        </div>
                        <p className="text-xs text-app-muted mt-0.5 line-clamp-2">{n.message}</p>
                        <p className="text-[10px] text-app-muted mt-1">{timeAgo(n.created_at)}</p>
                      </div>
                    </button>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
